import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  App,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  theme,
} from 'antd';
import {
  SafetyCertificateOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { PageHeader, CardHeader } from 'components/headings';
import useGarantiasApi from 'pages/admin/garantias/useGarantiasApi';

const { useToken } = theme;
const { TextArea } = Input;

// Pantalla del super-tenant (liftlogic) para administrar el catálogo de
// garantías de un tenant específico. Accedida desde TenantEditPage → botón
// "Garantías". Calcada del patrón de TenantMenuItemsPage (tenantId por
// path param, tenantName por location.state) y del layout PageHeader +
// panelStyle + CardHeader + Table (memoria feedback_gestion_list_pattern).
function TenantGarantiasPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();
  const { token } = useToken();
  const api = useGarantiasApi();

  const tenantName = location.state?.tenantName || `Tenant ${tenantId}`;
  const idNum = Number(tenantId);

  const [garantias, setGarantias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.list(idNum);
      setGarantias(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'No se pudo cargar el catálogo de garantías');
    } finally {
      setLoading(false);
    }
  }, [api, idNum, message]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ orden: 0, activo: true });
    setOpen(true);
  };

  const onEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      nombre: row.nombre,
      cuerpo: row.cuerpo,
      orden: row.orden,
      activo: row.activo,
    });
    setOpen(true);
  };

  const onSubmit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = {
        nombre: values.nombre,
        cuerpo: values.cuerpo,
        orden: values.orden ?? 0,
        activo: values.activo ?? true,
      };
      if (editing) {
        await api.update(editing.id, idNum, payload);
      } else {
        await api.create({ ...payload, tenantId: idNum });
      }
      message.success('Garantía guardada');
      setOpen(false);
      await refresh();
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'Error al guardar la garantía');
    } finally {
      setSaving(false);
    }
  };

  const onToggleActivo = async (row) => {
    try {
      await api.update(row.id, idNum, { activo: !row.activo });
      message.success(row.activo ? 'Garantía dada de baja' : 'Garantía activada');
      await refresh();
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'Error al actualizar la garantía');
    }
  };

  const onDelete = async (row) => {
    try {
      await api.remove(row.id, idNum);
      message.success('Garantía eliminada');
      await refresh();
    } catch (err) {
      // I3 (review 2026-07-13): el backend rechaza el borrado de una garantía
      // en uso con 409 y un mensaje explicando que hay que darla de baja en
      // su lugar. `errorHandler` responde `{ message }` (no `mensaje`); se
      // revisan ambos por compatibilidad con otros formatos de error.
      message.error(
        err?.response?.data?.message ||
          err?.response?.data?.mensaje ||
          'No se pudo eliminar la garantía'
      );
    }
  };

  const panelStyle = useMemo(
    () => ({
      border: `1px solid ${token.colorBorder}`,
      borderRadius: token.borderRadiusLG,
      overflow: 'hidden',
      background: token.colorBgContainer,
      boxShadow: token.boxShadowSecondary,
    }),
    [token]
  );

  const columns = [
    { title: 'Nombre', dataIndex: 'nombre' },
    { title: 'Orden', dataIndex: 'orden', width: 100 },
    {
      title: 'Estado',
      dataIndex: 'activo',
      width: 130,
      render: (activo) => (
        <Tag color={activo ? 'green' : 'default'}>{activo ? 'Activa' : 'Dada de baja'}</Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 160,
      render: (_, row) => (
        <Space size="small">
          <Tooltip title="Editar">
            <Button icon={<EditOutlined />} size="small" onClick={() => onEdit(row)} />
          </Tooltip>
          <Popconfirm
            title={
              row.activo
                ? 'Dar de baja esta garantía. Los presupuestos que ya la usan conservan su texto tal como se emitieron; solo deja de aparecer en el desplegable del ejecutivo. ¿Continuar?'
                : '¿Activar esta garantía para que vuelva a aparecer en el desplegable del ejecutivo?'
            }
            okText="Sí"
            cancelText="No"
            onConfirm={() => onToggleActivo(row)}
            overlayStyle={{ maxWidth: 320 }}
          >
            <Tooltip title={row.activo ? 'Dar de baja' : 'Activar'}>
              <Button
                icon={row.activo ? <StopOutlined /> : <CheckCircleOutlined />}
                size="small"
                danger={row.activo}
              />
            </Tooltip>
          </Popconfirm>
          <Popconfirm
            title="¿Eliminar esta garantía del catálogo? Esta acción no se puede deshacer."
            okText="Sí"
            cancelText="No"
            onConfirm={() => onDelete(row)}
          >
            <Tooltip title="Eliminar">
              <Button icon={<DeleteOutlined />} size="small" danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <PageHeader
        icon={SafetyCertificateOutlined}
        title="Garantías"
        subtitle={`Tenant: ${tenantName}`}
        actions={
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>
              Nueva garantía
            </Button>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(`/admin/tenants/${tenantId}/edit`, { state: location.state })}
            >
              Volver al tenant
            </Button>
          </Space>
        }
      />

      <div style={panelStyle}>
        <CardHeader
          icon={SafetyCertificateOutlined}
          title="Catálogo de garantías"
          subtitle="Cláusulas de garantía disponibles para presupuestos de este tenant"
          action={
            <Tag color="default" style={{ margin: 0, fontWeight: 600 }}>
              {garantias.length} {garantias.length === 1 ? 'garantía' : 'garantías'}
            </Tag>
          }
        />
        <div style={{ padding: token.paddingSM }}>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={garantias}
            pagination={false}
          />
        </div>
      </div>

      <Modal
        open={open}
        title={editing ? 'Editar garantía' : 'Nueva garantía'}
        onCancel={() => setOpen(false)}
        onOk={onSubmit}
        confirmLoading={saving}
        okText="Guardar"
        cancelText="Cancelar"
        destroyOnHidden
        width={640}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Nombre"
            name="nombre"
            rules={[{ required: true, message: 'El nombre es obligatorio' }]}
            extra="Es el texto que ve el ejecutivo en el desplegable al armar el presupuesto."
          >
            <Input placeholder="Reemplazo de repuestos" />
          </Form.Item>
          <Form.Item
            label="Cláusula"
            name="cuerpo"
            rules={[{ required: true, message: 'La cláusula es obligatoria' }]}
            extra={
              'Escriba {meses} donde tenga que aparecer el plazo. Al guardar el presupuesto, ' +
              'ese texto queda fijo en el documento: si después se edita la cláusula, los ' +
              'presupuestos ya emitidos no cambian.'
            }
          >
            <TextArea
              rows={8}
              placeholder="Garantiza el reemplazo de piezas por {meses} meses a partir de la fecha de entrega."
            />
          </Form.Item>
          <Form.Item
            label="Orden"
            name="orden"
            extra="Orden de aparición en el desplegable del ejecutivo (el número más bajo aparece primero)."
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="Activa"
            name="activo"
            valuePropName="checked"
            extra="Si está inactiva, no aparece en el desplegable del ejecutivo. Los presupuestos que ya la usan conservan su texto igual."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default TenantGarantiasPage;
